"use client";

import { c as _c } from "react/compiler-runtime";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import TransferPanel from "@/components/nft-transfer/TransferPanel";
import { useTransfer } from "@/components/nft-transfer/TransferState";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { publicEnv } from "@/config/env";
import type { CollectedCard } from "@/entities/IProfile";
import { CollectedCollectionType, CollectionSeized, CollectionSort } from "@/entities/IProfile";
import type { MemeSeason } from "@/entities/ISeason";
import { SortDirection } from "@/entities/ISort";
import type { ApiIdentity } from "@/generated/models/ObjectSerializer";
import { areEqualAddresses } from "@/helpers/Helpers";
import type { Page } from "@/helpers/Types";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { fetchAllPages } from "@/services/6529api";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import UserPageCollectedCards from "./cards/UserPageCollectedCards";
import UserPageCollectedNetworkCards from "./cards/UserPageCollectedNetworkCards";
import { COLLECTED_COLLECTIONS_META, convertAddressToLowerCase } from "./filters/user-page-collected-filters.helpers";
import UserPageCollectedFilters from "./filters/UserPageCollectedFilters";
import { useXtdhTokensQuery } from "./hooks/useXtdhTokensQuery";
import UserPageCollectedFirstLoading from "./UserPageCollectedFirstLoading";
export interface ProfileCollectedFilters {
  readonly handleOrWallet: string;
  readonly accountForConsolidations: boolean;
  readonly collection: CollectedCollectionType | null;
  readonly subcollection: string | null;
  readonly seized: CollectionSeized | null;
  readonly szn: MemeSeason | null;
  readonly initialSznId: number | null;
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy: CollectionSort;
  readonly sortDirection: SortDirection;
}
interface QueryUpdateInput {
  name: keyof typeof SEARCH_PARAMS_FIELDS;
  value: string | null;
}
const SEARCH_PARAMS_FIELDS = {
  address: "address",
  collection: "collection",
  subcollection: "subcollection",
  seized: "seized",
  szn: "szn",
  page: "page",
  sortBy: "sort-by",
  sortDirection: "sort-direction"
} as const;
export default function UserPageCollected(t0) {
  const $ = _c(123);
  const {
    profile
  } = t0;
  const {
    address: connectedAddress
  } = useSeizeConnectContext();
  const isMobile = useIsMobileScreen();
  const defaultSortBy = CollectionSort.TOKEN_ID;
  const defaultSortDirection = SortDirection.DESC;
  const defaultSeized = CollectionSeized.SEIZED;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const router = useRouter();
  let t1;
  if ($[0] !== params?.user) {
    t1 = params?.user?.toString().toLowerCase() ?? "";
    $[0] = params?.user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const user = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const {
        seized,
        collection
      } = t3;
      if (!collection) {
        return defaultSeized;
      }
      if (!COLLECTED_COLLECTIONS_META[collection].filters.seized) {
        return defaultSeized;
      }
      if (!seized) {
        return null;
      }
      return Object.values(CollectionSeized).find(c => c === seized.toUpperCase()) ?? null;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const convertSeized = t2;
  const convertSznId = _temp;
  const convertCollection = _temp2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const {
        sortBy,
        collection: collection_2
      } = t4;
      if (!sortBy) {
        return defaultSortBy;
      }
      if (collection_2 && collection_2 !== CollectedCollectionType.NETWORK && !COLLECTED_COLLECTIONS_META[collection_2].filters.sort.includes(sortBy.toUpperCase() as CollectionSort)) {
        return defaultSortBy;
      }
      return Object.values(CollectionSort).find(c_1 => c_1 === sortBy.toUpperCase()) ?? defaultSortBy;
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const convertSortedBy = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = sortDirection => {
      if (!sortDirection) {
        return defaultSortDirection;
      }
      return Object.values(SortDirection).find(c_2 => c_2 === sortDirection.toUpperCase()) ?? defaultSortDirection;
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const convertSortDirection = t4;
  let t5;
  if ($[5] !== profile.handle || $[6] !== searchParams || $[7] !== user) {
    t5 = () => {
      const address = searchParams?.get(SEARCH_PARAMS_FIELDS.address);
      const collection_3 = searchParams?.get(SEARCH_PARAMS_FIELDS.collection);
      const subcollection = searchParams?.get(SEARCH_PARAMS_FIELDS.subcollection);
      const seized_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.seized);
      const szn_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.szn);
      const page = searchParams?.get(SEARCH_PARAMS_FIELDS.page);
      const sortBy_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.sortBy);
      const sortDirection_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.sortDirection);
      const convertedAddress = convertAddressToLowerCase(address);
      const convertedCollection = convertCollection(collection_3 ?? null);
      return {
        handleOrWallet: convertedAddress ?? profile.handle ?? user,
        accountForConsolidations: !convertedAddress,
        collection: convertedCollection,
        subcollection: subcollection ?? null,
        seized: convertSeized({
          seized: seized_0 ?? null,
          collection: convertedCollection
        }),
        szn: null,
        initialSznId: convertSznId({
          szn: szn_0 ?? null,
          collection: convertedCollection
        }),
        page: page ? Number.parseInt(page, 10) : 1,
        pageSize: 24,
        sortBy: convertSortedBy({
          sortBy: sortBy_0 ?? null,
          collection: convertedCollection
        }),
        sortDirection: convertSortDirection(sortDirection_0 ?? null)
      };
    };
    $[5] = profile.handle;
    $[6] = searchParams;
    $[7] = user;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const getFilters = t5;
  let t6;
  if ($[9] !== searchParams) {
    t6 = updateItems => {
      const params_0 = new URLSearchParams(searchParams?.toString() ?? "");
      for (const {
        name,
        value
      } of updateItems) {
        const key = SEARCH_PARAMS_FIELDS[name];
        if (!value) {
          params_0.delete(key);
        } else {
          params_0.set(key, value.toLowerCase());
        }
      }
      return params_0.toString();
    };
    $[9] = searchParams;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const createQueryString = t6;
  let t7;
  if ($[11] !== createQueryString || $[12] !== pathname || $[13] !== router) {
    t7 = async updateItems_0 => {
      const queryString = createQueryString(updateItems_0);
      const path = queryString ? pathname + "?" + queryString : pathname;
      if (path) {
        router.replace(path, {
          scroll: false
        });
      }
    };
    $[11] = createQueryString;
    $[12] = pathname;
    $[13] = router;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const updateFields = t7;
  let t8;
  if ($[15] !== getFilters) {
    t8 = getFilters();
    $[15] = getFilters;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const [filters, setFilters] = useState(t8);
  const {
    enabled: transferEnabled
  } = useTransfer();
  let t9;
  if ($[17] !== filters.collection || $[18] !== filters.sortBy || $[19] !== updateFields) {
    t9 = async collection_4 => {
      if (!filters.collection && !collection_4) {
        return;
      }
      const items = [{
        name: "collection",
        value: filters.collection === collection_4 ? null : collection_4 ?? null
      }, {
        name: "page",
        value: "1"
      }, {
        name: "seized",
        value: defaultSeized
      }, {
        name: "szn",
        value: null
      }, {
        name: "subcollection",
        value: null
      }];
      const isSwitchingFromNetwork = filters.collection === CollectedCollectionType.NETWORK;
      if (collection_4 && collection_4 !== CollectedCollectionType.NETWORK && !COLLECTED_COLLECTIONS_META[collection_4].filters.sort.includes(filters.sortBy) || isSwitchingFromNetwork) {
        items.push({
          name: "sortBy",
          value: CollectionSort.TOKEN_ID
        });
        items.push({
          name: "sortDirection",
          value: SortDirection.DESC
        });
      } else {
        if (collection_4 === CollectedCollectionType.NETWORK) {
          items.push({
            name: "sortBy",
            value: CollectionSort.XTDH
          });
          items.push({
            name: "sortDirection",
            value: SortDirection.DESC
          });
        }
      }
      await updateFields(items);
    };
    $[17] = filters.collection;
    $[18] = filters.sortBy;
    $[19] = updateFields;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const setCollection = t9;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = t11 => {
      const {
        newSortBy,
        currentSortBy,
        currentSortDirection
      } = t11;
      if (newSortBy === currentSortBy) {
        if (currentSortDirection === SortDirection.ASC) {
          return SortDirection.DESC;
        }
        return SortDirection.ASC;
      }
      return defaultSortDirection;
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const calculateSortDirection = t10;
  let t11;
  if ($[22] !== filters.sortBy || $[23] !== filters.sortDirection || $[24] !== updateFields) {
    t11 = async sortBy_1 => {
      const items_0 = [{
        name: "sortBy",
        value: sortBy_1
      }, {
        name: "sortDirection",
        value: calculateSortDirection({
          newSortBy: sortBy_1,
          currentSortBy: filters.sortBy,
          currentSortDirection: filters.sortDirection
        })
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items_0);
    };
    $[22] = filters.sortBy;
    $[23] = filters.sortDirection;
    $[24] = updateFields;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const setSortBy = t11;
  let t12;
  if ($[26] !== updateFields) {
    t12 = async seized_1 => {
      const items_1 = [{
        name: "seized",
        value: seized_1
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items_1);
    };
    $[26] = updateFields;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const setSeized = t12;
  let t13;
  if ($[28] !== updateFields) {
    t13 = async szn_1 => {
      setFilters(prev => ({
        ...prev,
        szn: szn_1
      }));
      const items_2 = [{
        name: "szn",
        value: szn_1 ? szn_1.id.toString() : null
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items_2);
    };
    $[28] = updateFields;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const setSzn = t13;
  let t14;
  if ($[30] !== updateFields) {
    t14 = async subcollection_0 => {
      const items_3 = [{
        name: "subcollection",
        value: subcollection_0
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items_3);
    };
    $[30] = updateFields;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const setSubcollection = t14;
  let t15;
  if ($[32] !== updateFields) {
    t15 = async page_0 => {
      const items_4 = [{
        name: "page",
        value: page_0.toString()
      }];
      await updateFields(items_4);
    };
    $[32] = updateFields;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const setPage = t15;
  let t16;
  if ($[34] !== getFilters) {
    t16 = () => {
      setFilters(prev_0 => {
        const newFilters = getFilters();
        if (prev_0.szn && newFilters.initialSznId === prev_0.szn.id) {
          return {
            ...newFilters,
            szn: prev_0.szn
          };
        }
        return newFilters;
      });
    };
    $[34] = getFilters;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== connectedAddress || $[37] !== getFilters || $[38] !== profile || $[39] !== searchParams || $[40] !== user) {
    t17 = [searchParams, profile, user, connectedAddress, getFilters];
    $[36] = connectedAddress;
    $[37] = getFilters;
    $[38] = profile;
    $[39] = searchParams;
    $[40] = user;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  useEffect(t16, t17);
  let t18;
  if ($[42] !== filters) {
    t18 = [QueryKey.PROFILE_COLLECTED, filters];
    $[42] = filters;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== filters.accountForConsolidations || $[45] !== filters.collection || $[46] !== filters.handleOrWallet || $[47] !== filters.page || $[48] !== filters.pageSize || $[49] !== filters.seized || $[50] !== filters.sortBy || $[51] !== filters.sortDirection || $[52] !== filters.szn) {
    t19 = async () => {
      const params_1 = {
        page: filters.page.toString(),
        page_size: filters.pageSize.toString(),
        sort: filters.sortBy.toLowerCase(),
        sort_direction: filters.sortDirection
      };
      if (!filters.accountForConsolidations) {
        params_1.account_for_consolidations = "false";
      }
      if (filters.collection) {
        params_1.collection = filters.collection;
      }
      if (filters.seized) {
        params_1.seized = filters.seized;
      }
      if (filters.szn) {
        params_1.szn = filters.szn.id.toString();
      }
      return await commonApiFetch({
        endpoint: `profiles/${filters.handleOrWallet}/collected`,
        params: params_1
      });
    };
    $[44] = filters.accountForConsolidations;
    $[45] = filters.collection;
    $[46] = filters.handleOrWallet;
    $[47] = filters.page;
    $[48] = filters.pageSize;
    $[49] = filters.seized;
    $[50] = filters.sortBy;
    $[51] = filters.sortDirection;
    $[52] = filters.szn;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const t20 = filters.collection !== CollectedCollectionType.NETWORK;
  let t21;
  if ($[54] !== t18 || $[55] !== t19 || $[56] !== t20) {
    t21 = {
      queryKey: t18,
      queryFn: t19,
      placeholderData: keepPreviousData,
      enabled: t20
    };
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  const {
    isFetching,
    isLoading: isInitialLoading,
    data
  } = useQuery(t21);
  let t22;
  if ($[58] !== filters.handleOrWallet || $[59] !== filters.page || $[60] !== filters.pageSize || $[61] !== filters.sortBy || $[62] !== filters.sortDirection || $[63] !== filters.subcollection) {
    t22 = {
      identity: filters.handleOrWallet,
      page: filters.page,
      pageSize: filters.pageSize,
      sort: filters.sortBy,
      order: filters.sortDirection,
      contract: filters.subcollection
    };
    $[58] = filters.handleOrWallet;
    $[59] = filters.page;
    $[60] = filters.pageSize;
    $[61] = filters.sortBy;
    $[62] = filters.sortDirection;
    $[63] = filters.subcollection;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const {
    data: dataNetwork,
    isLoading: isNetworkLoading,
    isFetching: isNetworkFetching
  } = useXtdhTokensQuery(t22);
  const isNetwork = filters.collection === CollectedCollectionType.NETWORK;
  const isFetchingData = isFetching || isNetworkFetching;
  const isLoading = isInitialLoading || isNetworkLoading;
  let t23;
  if ($[65] !== connectedAddress || $[66] !== data || $[67] !== isMobile || $[68] !== profile.wallets) {
    t23 = !isMobile && !!(profile.wallets?.some(w => areEqualAddresses(w.wallet, connectedAddress)) && data?.data && data.data.length > 0);
    $[65] = connectedAddress;
    $[66] = data;
    $[67] = isMobile;
    $[68] = profile.wallets;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  const showTransfer = t23;
  let t24;
  if ($[70] !== connectedAddress || $[71] !== filters) {
    t24 = [QueryKey.PROFILE_COLLECTED_TRANSFER, filters, connectedAddress];
    $[70] = connectedAddress;
    $[71] = filters;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== connectedAddress) {
    t25 = async () => {
      ;
      try {
        const allPagesUrl = `${publicEnv.API_ENDPOINT}/api/profiles/${connectedAddress}/collected?page_size=200&seized=${CollectionSeized.SEIZED}`;
        return await fetchAllPages(allPagesUrl);
      } catch (t26) {
        const error = t26;
        console.error(`Failed to fetch transfer data for profile ${connectedAddress}`, error);
        throw error;
      }
    };
    $[73] = connectedAddress;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  const t26 = showTransfer && transferEnabled;
  let t27;
  if ($[75] !== t24 || $[76] !== t25 || $[77] !== t26) {
    t27 = {
      queryKey: t24,
      queryFn: t25,
      enabled: t26,
      placeholderData: keepPreviousData
    };
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  const {
    isFetching: isFetchingTransfer,
    data: dataTransfer
  } = useQuery(t27);
  const [totalPages, setTotalPages] = useState(1);
  let t28;
  if ($[79] !== data || $[80] !== filters.page || $[81] !== filters.pageSize || $[82] !== isFetchingData || $[83] !== isNetwork || $[84] !== setPage) {
    t28 = () => {
      if (isFetchingData) {
        return;
      }
      if (isNetwork) {
        return;
      }
      if (!data?.count) {
        setPage(1);
        setTotalPages(1);
        return;
      }
      const pagesCount = Math.ceil(data.count / filters.pageSize);
      if (pagesCount < filters.page) {
        setPage(pagesCount);
        return;
      }
      setTotalPages(pagesCount);
    };
    $[79] = data;
    $[80] = filters.page;
    $[81] = filters.pageSize;
    $[82] = isFetchingData;
    $[83] = isNetwork;
    $[84] = setPage;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  const t29 = data?.count;
  const t30 = data?.page;
  let t31;
  if ($[86] !== filters.page || $[87] !== filters.pageSize || $[88] !== isFetchingData || $[89] !== isNetwork || $[90] !== t29 || $[91] !== t30) {
    t31 = [t29, t30, isFetchingData, isNetwork, filters.pageSize, filters.page];
    $[86] = filters.page;
    $[87] = filters.pageSize;
    $[88] = isFetchingData;
    $[89] = isNetwork;
    $[90] = t29;
    $[91] = t30;
    $[92] = t31;
  } else {
    t31 = $[92];
  }
  useEffect(t28, t31);
  let t32;
  if ($[93] !== filters.collection) {
    t32 = () => filters.collection ? COLLECTED_COLLECTIONS_META[filters.collection].showCardDataRow : true;
    $[93] = filters.collection;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  const getShowDataRow = t32;
  let t33;
  if ($[95] !== getShowDataRow) {
    t33 = getShowDataRow();
    $[95] = getShowDataRow;
    $[96] = t33;
  } else {
    t33 = $[96];
  }
  const [showDataRow, setShowDataRow] = useState(t33);
  let t34;
  if ($[97] !== getShowDataRow || $[98] !== isNetwork) {
    t34 = () => {
      if (isNetwork) {
        setShowDataRow(false);
      } else {
        setShowDataRow(getShowDataRow());
      }
    };
    $[97] = getShowDataRow;
    $[98] = isNetwork;
    $[99] = t34;
  } else {
    t34 = $[99];
  }
  let t35;
  if ($[100] !== filters.collection || $[101] !== isNetwork) {
    t35 = [filters.collection, isNetwork];
    $[100] = filters.collection;
    $[101] = isNetwork;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  useEffect(t34, t35);
  const scrollContainer = useRef(null);
  let t36;
  if ($[103] !== data?.data || $[104] !== dataNetwork?.data || $[105] !== dataNetwork?.next || $[106] !== dataTransfer || $[107] !== filters || $[108] !== isFetchingTransfer || $[109] !== isLoading || $[110] !== isNetwork || $[111] !== profile || $[112] !== setCollection || $[113] !== setPage || $[114] !== setSeized || $[115] !== setSortBy || $[116] !== setSubcollection || $[117] !== setSzn || $[118] !== showDataRow || $[119] !== showTransfer || $[120] !== totalPages || $[121] !== transferEnabled) {
    t36 = <div className="tailwind-scope">{isLoading ? <UserPageCollectedFirstLoading /> : <><div ref={scrollContainer}><UserPageCollectedFilters profile={profile} filters={filters} containerRef={scrollContainer} setCollection={setCollection} setSortBy={setSortBy} setSeized={setSeized} setSzn={setSzn} setSubcollection={setSubcollection} showTransfer={showTransfer} /></div><div className="tw-mt-6 tw-flex tw-gap-6">{isNetwork ? <UserPageCollectedNetworkCards cards={dataNetwork?.data ?? []} page={filters.page} setPage={setPage} next={dataNetwork?.next ?? false} /> : <UserPageCollectedCards cards={data?.data ?? []} totalPages={totalPages} page={filters.page} showDataRow={showDataRow} filters={filters} setPage={setPage} dataTransfer={dataTransfer ?? []} isTransferLoading={isFetchingTransfer} />}</div>{showTransfer && transferEnabled && <TransferPanel isLoading={isFetchingTransfer} />}</>}</div>;
    $[103] = data?.data;
    $[104] = dataNetwork?.data;
    $[105] = dataNetwork?.next;
    $[106] = dataTransfer;
    $[107] = filters;
    $[108] = isFetchingTransfer;
    $[109] = isLoading;
    $[110] = isNetwork;
    $[111] = profile;
    $[112] = setCollection;
    $[113] = setPage;
    $[114] = setSeized;
    $[115] = setSortBy;
    $[116] = setSubcollection;
    $[117] = setSzn;
    $[118] = showDataRow;
    $[119] = showTransfer;
    $[120] = totalPages;
    $[121] = transferEnabled;
    $[122] = t36;
  } else {
    t36 = $[122];
  }
  return t36;
}
function _temp2(collection_1) {
  if (!collection_1) {
    return null;
  }
  return Object.values(CollectedCollectionType).find(c_0 => c_0 === collection_1.toUpperCase()) ?? null;
}
function _temp(t0) {
  const {
    szn,
    collection: collection_0
  } = t0;
  if (!collection_0) {
    return null;
  }
  if (!COLLECTED_COLLECTIONS_META[collection_0].filters.szn) {
    return null;
  }
  if (!szn) {
    return null;
  }
  const parsed = Number.parseInt(szn, 10);
  return Number.isNaN(parsed) ? null : parsed;
}